import { EoTrackerUpdate } from './../models/eo-tracker-updates.model';
import { RequestQueryBuilder, SCondition } from '@nestjsx/crud-request';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { TorchAPIConfiguration } from '../../../config/api.configuration';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Burly } from 'kb-burly';
import { ManyResponse } from '../../../responses/many.response';
import { EoModel } from '../models';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TorchEoTrackerService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  public queryTrackedEos(pageSize: number = 15,
                         currentPage: number = 0,
                         sortDirection: 'ASC' | 'DESC' | '' = 'DESC',
                         sortBy: string = 'id',
                         filters: SCondition | null): Observable<ManyResponse<EoModel>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({ field: sortBy, order: sortDirection });
    }

    if (!!filters) {
      qb.search(filters);
    }


    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<ManyResponse<EoModel>>(endpoint);
  }

  public getFleets(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addSegment('/fleets')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ FLEET: string }]>(endpoint).pipe(
      map(response => response.map(v => v.FLEET))
    );
  }

  public getATAs(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addSegment('/ATAs')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ PRIMARY_ATA: string }]>(endpoint).pipe(
      map(response => response.map(v => v.PRIMARY_ATA))
    );
  }

  public getEoTypes(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addSegment('/eoTypes')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ EO_TYPE: string }]>(endpoint).pipe(
      map(response => response.map(v => v.EO_TYPE))
    );
  }

  public getEoList(): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addSegment('/eoList')
      .get;
    return this.httpClient.get<[{ EO_NBR: string }]>(endpoint).pipe(
      map(response => response.map(v => v.EO_NBR))
    );
  }

  public getEprsList(): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addSegment('/eprsList')
      .get;
    return this.httpClient.get<[{ EPRS_NBR: string }]>(endpoint).pipe(
      map(response => response.map(v => v.EPRS_NBR))
    );
  }

  public getEpraList(): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addSegment('/epraList')
      .get;
    return this.httpClient.get<[{ EPRA_NBR: string }]>(endpoint).pipe(
      map(response => response.map(v => v.EPRA_NBR))
    );
  }

  public exportToExcel(trackedEos: EoModel[]) {
    return new Observable(subscriber => {
      let endpoint = Burly(this.config.rootUrl)
        .addSegment('/eng-support-eo-tracker')
        .addSegment('/export')
        .get;

      const httpOptions: { headers; observe; responseType; } = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response',
        responseType: 'blob'
      };

      const body = {
        trackedEos: trackedEos
      };
      this.httpClient.post(endpoint, body, httpOptions).subscribe((response) => {
        subscriber.next((response as unknown as HttpResponse<any>).body);
        subscriber.complete();
      });
    });
  }

  public deleteTrackedEo(trackedEo: EoModel) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addSegment('/' + trackedEo.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        // eslint-disable-next-line no-shadow
        .subscribe((trackedEo) => {
          resolve(trackedEo);
        }, reject);
    });
  }

  public addTrackedEo(trackedEo: EoModel) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, trackedEo)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public saveRequest(trackedEo: EoModel) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addSegment('/' + trackedEo.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.patch(endpointURL, trackedEo)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  getEoTrackerById(projectId: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker')
      .addQuery('filter', 'id||$eq||' + projectId)
      .get;

    return this.httpClient.get<ManyResponse<EoModel>>(endpoint).pipe(
      map(response => response.data)
    );
  }

  getProjectUpdates(projectId: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker-updates')
      .addQuery('filter', 'projectId||$eq||' + projectId)
      .get;

    return this.httpClient.get<ManyResponse<EoTrackerUpdate>>(endpoint).pipe(
      map(response => response.data)
    );
  }

  public addProjectUpdate(update: EoTrackerUpdate) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker-updates')
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.post(endpointURL, update)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }

  public deleteProjectUpdate(update: EoTrackerUpdate) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/eng-support-eo-tracker-updates')
      .addSegment('/' + update.id)
      .get;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(endpointURL)
        .subscribe((response) => {
          resolve(response);
        }, reject);
    });
  }
}
